/**
 * ToolsPane - Tools Panel Container
 *
 * Container component for the tools panel with tabs:
 * - Analyze: Content analysis tools
 * - Transform: AI transformation tools
 * - Harvest: Content extraction
 * - Transcribe: Media transcription launcher
 *
 * @module @humanizer/studio/components/tools/ToolsPane
 */

import React, { useCallback } from 'react';
import { usePanels, usePanelState } from '../../contexts/PanelContext';
import { AnalyzeTool } from './AnalyzeTool';
import { TransformTool } from './TransformTool';
import { TranscribeTool } from './TranscribeTool';
import { BufferPicker } from '../workspace/BufferPicker';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import type { SentenceStepResult, SentencingV2Result, NoemaGraphResult, SicResult, ChapterGraphResult } from '../../types/analysis';
import type { SplitContent } from '../workspace/workspace-types';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ToolsPaneProps {
  /** Archive ID for search/harvest context */
  archiveId?: string;
  /** Currently selected content IDs for transformation/harvest */
  selectedContentIds?: string[];
  /** Called when search finds results */
  onSearchResults?: (results: SearchResult[]) => void;
  /** Called when transformation is applied */
  onTransformApplied?: (contentId: string, transformedContent: string) => void;
  /** Called when content is harvested */
  onContentHarvested?: (items: HarvestedItem[]) => void;
  /** Called when transcription is requested */
  onRequestTranscription?: (mediaId: string, archiveId: string, type: TranscriptionType) => void;
  /** Called when transform applies and split view is wanted */
  onSplitRequested?: (content: SplitContent) => void;
  /** Called when sentencing analysis completes with step data */
  onSentencingComplete?: (steps: SentenceStepResult['step'][]) => void;
  /** Called when role classifier (sentencing v2) completes */
  onRoleClassifierComplete?: (data: SentencingV2Result | null) => void;
  /** Called when noema graph analysis completes */
  onNoemaGraphComplete?: (data: NoemaGraphResult | null) => void;
  /** Called when SIC analysis completes */
  onSicComplete?: (data: SicResult | null) => void;
  /** Called when chapter graph analysis completes */
  onChapterGraphComplete?: (data: ChapterGraphResult | null) => void;
  /** Current workspace view mode (from MainWorkspace) */
  viewMode?: string;
  /** SIC analysis results for subjective authenticity guidance in humanization */
  sicData?: SicResult | null;
  /** Optional class name */
  className?: string;
}

/** Search result from agentic search */
export interface SearchResult {
  id: string;
  type: 'content' | 'transcript' | 'cluster';
  content: string;
  score: number;
  sourceId?: string;
  metadata?: Record<string, unknown>;
}

/** Harvested content item */
export interface HarvestedItem {
  id: string;
  content: string;
  source: string;
  addedAt: Date;
}

/** Transcription types */
export type TranscriptionType = 'audio' | 'ocr' | 'caption' | 'description' | 'manual';

// Tab configuration
const TABS = [
  { id: 'analyze', label: 'Analyze', icon: '🔬' },
  { id: 'transform', label: 'Transform', icon: '✨' },
  { id: 'transcribe', label: 'Transcribe', icon: '🎙️' },
] as const;

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/** Mode-specific context hints for the tools panel */
const VIEW_MODE_HINTS: Record<string, { label: string; tip: string }> = {
  read: { label: 'Reading', tip: 'Analyze or transform the current buffer' },
  edit: { label: 'Editing', tip: 'Transform selected text or analyze your edits' },
  split: { label: 'Comparing', tip: 'Analyze differences between versions' },
  stack: { label: 'Stacking', tip: 'Analyze individual cards or compare sources' },
  outline: { label: 'Outlining', tip: 'Analyze source coverage or refine structure' },
  draft: { label: 'Drafting', tip: 'Check AI detection, humanize, or style-match' },
};

export function ToolsPane({
  archiveId,
  selectedContentIds = [],
  onSearchResults,
  onTransformApplied,
  onContentHarvested,
  onRequestTranscription,
  onSplitRequested,
  onSentencingComplete,
  onRoleClassifierComplete,
  onNoemaGraphComplete,
  onSicComplete,
  onChapterGraphComplete,
  viewMode,
  sicData,
  className = '',
}: ToolsPaneProps): React.ReactElement {
  const panelState = usePanelState();
  const { setToolsTab, toggleTools } = usePanels();

  const handleTabChange = useCallback(
    (tabId: typeof TABS[number]['id']) => {
      setToolsTab(tabId);
    },
    [setToolsTab]
  );

  // Render tab content
  const renderTabContent = () => {
    switch (panelState.toolsTab) {
      case 'analyze':
        return (
          <ErrorBoundary fallback="panel" label="Analyze tool">
            <AnalyzeTool onSentencingComplete={onSentencingComplete} onRoleClassifierComplete={onRoleClassifierComplete} onNoemaGraphComplete={onNoemaGraphComplete} onSicComplete={onSicComplete} onChapterGraphComplete={onChapterGraphComplete} />
          </ErrorBoundary>
        );
      case 'transform':
        return (
          <ErrorBoundary fallback="panel" label="Transform tool">
            <TransformTool
              selectedContentIds={selectedContentIds}
              onTransformApplied={onTransformApplied}
              onSplitRequested={onSplitRequested}
              sicData={sicData}
            />
          </ErrorBoundary>
        );
      case 'transcribe':
        return (
          <ErrorBoundary fallback="panel" label="Transcribe tool">
            <TranscribeTool
              archiveId={archiveId}
              onRequestTranscription={onRequestTranscription}
              onSplitRequested={onSplitRequested}
            />
          </ErrorBoundary>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`tools-pane ${className}`}>
      {/* Panel Header */}
      <div className="panel__header">
        <h2 className="panel__title">Tools</h2>
        <div className="panel__actions">
          {!panelState.isMobile && (
            <button
              className="panel__collapse-btn"
              onClick={toggleTools}
              aria-label="Collapse tools panel"
              title="Collapse (Cmd+])"
            >
              ›
            </button>
          )}
        </div>
      </div>

      {/* View mode context hint */}
      {viewMode && VIEW_MODE_HINTS[viewMode] && (
        <div className="tools-pane__mode-hint">
          <span className="tools-pane__mode-label">{VIEW_MODE_HINTS[viewMode].label}</span>
          <span className="tools-pane__mode-tip">{VIEW_MODE_HINTS[viewMode].tip}</span>
        </div>
      )}

      {/* Buffer Picker */}
      <div className="tools-pane__buffer-section">
        <BufferPicker />
      </div>

      {/* Tabs */}
      <div className="panel__tabs" role="tablist" aria-label="Tool views">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`panel__tab ${panelState.toolsTab === tab.id ? 'panel__tab--active' : ''}`}
            role="tab"
            aria-selected={panelState.toolsTab === tab.id}
            aria-controls={`tools-panel-${tab.id}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <span aria-hidden="true">{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        className="panel__content"
        role="tabpanel"
        id={`tools-panel-${panelState.toolsTab}`}
        aria-labelledby={`tools-tab-${panelState.toolsTab}`}
      >
        {renderTabContent()}
      </div>
    </div>
  );
}

export default ToolsPane;
